import * as React from 'react';

/**
 * Custom debounce hook that delays the execution of a function
 * similar to Lodash's debounce.
 *
 * A pending call is cancelled when `callback` or `delay` changes identity, not just on unmount.
 * Callers must pass a referentially stable callback so a render does not cancel its own work.
 *
 * @param callback - The function to debounce. Must be referentially stable across renders.
 * @param delay - The delay in milliseconds.
 */
function useDebounce<TArgs extends unknown[]>(
	callback: (...args: TArgs) => void,
	delay: number
): (...args: TArgs) => void {
	const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

	const debouncedCallback = React.useCallback(
		(...args: TArgs) => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			timeoutRef.current = setTimeout(() => {
				timeoutRef.current = null;
				callback(...args);
			}, delay);
		},
		[callback, delay]
	);

	React.useEffect(
		() => () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		},
		[callback, delay]
	);

	return debouncedCallback;
}

export default useDebounce;
